/**
 * Team Design — Quick Customization input visibility gate.
 *
 * Shows / hides the team-name input and the logo-upload control based on
 * whether the collection's pattern SVGs actually carry `rect#team` /
 * `rect#logo` placeholders. If no card's pattern has a #team rect, the
 * team-name input is hidden (typing it would have nothing to anchor to).
 * Same gate for #logo. The font picker rides on the team gate too —
 * with no team text to render, there's nothing for a font to apply to,
 * so the font pane/tab follow the same `sawTeam` visibility.
 *
 * Evaluation happens "in the background" — we fetch each card's pattern
 * SVG (via the shared SvgEngine cache, so the fetch is reused by every
 * other consumer that needs the same SVG) and string-search the raw text
 * for `id="team"` / `id="logo"`. No SVG is mounted on the card just to
 * answer this question; that matters because the render pipeline is now
 * arm()-gated and won't paint cards until the shopper interacts. Probing
 * via the DOM would have been a chicken-and-egg: inputs hidden until SVG
 * renders, SVG won't render until inputs are interacted with.
 *
 * Presence is a one-way ratchet — once a placeholder is observed in any
 * fetched SVG, we keep that input shown for the rest of the page session
 * (a mixed collection where pattern A has #team and pattern B has #logo
 * still surfaces both controls).
 *
 * On mobile the panel uses tab nav (Color / Text / Logo); the matching
 * tab is hidden alongside its pane. If the currently-active tab happens
 * to be the one we hide, we activate the first remaining visible tab so
 * the pane area doesn't go blank. The font tile row is nested inside the
 * Text pane now, so it rides on the same team gate without needing its
 * own pane/tab selectors.
 *
 * Depends on:
 *   window.TeamDesignSvg   (svg-engine.js — for the shared fetch+cache)
 */
(function () {
  'use strict';

  var TEXT_PANE_SELECTOR = '.qc-pane[data-qc-pane="text"]';
  var LOGO_PANE_SELECTOR = '.qc-pane[data-qc-pane="logo"]';
  var TEXT_TAB_SELECTOR  = '.qc-tab[data-qc-tab="text"]';
  var LOGO_TAB_SELECTOR  = '.qc-tab[data-qc-tab="logo"]';

  // One-way ratchets — once a slot is seen we keep it shown. Flipping
  // back to false would cause flicker as cards are inspected one by one
  // and the first few may not carry the placeholder while later ones do.
  var sawTeam = false;
  var sawLogo = false;

  // Pattern-keyed dedupe: a collection often reuses the same pattern SVG
  // across many cards, no point string-searching the same text twice.
  var inspectedKeys = Object.create(null);

  // Regexes are anchored on the `id` attribute so we match both `<rect
  // id="team">` and `<g id="team">` while ignoring substrings like
  // `class="team"` or `id="teamText"`.
  var TEAM_RE = /\bid\s*=\s*["']team["']/;
  var LOGO_RE = /\bid\s*=\s*["']logo["']/;

  function setHidden(el, hide) {
    if (hide) el.setAttribute('hidden', '');
    else      el.removeAttribute('hidden');
  }

  function applyVisibility() {
    var panes = document.querySelectorAll(
      TEXT_PANE_SELECTOR + ',' + LOGO_PANE_SELECTOR
    );
    for (var i = 0; i < panes.length; i++) {
      var p   = panes[i];
      var key = p.getAttribute('data-qc-pane');
      var hide = key === 'logo' ? !sawLogo : !sawTeam;
      setHidden(p, hide);
    }

    var tabs = document.querySelectorAll(
      TEXT_TAB_SELECTOR + ',' + LOGO_TAB_SELECTOR
    );
    for (var j = 0; j < tabs.length; j++) {
      var t    = tabs[j];
      var key2 = t.getAttribute('data-qc-tab');
      var hide2 = key2 === 'logo' ? !sawLogo : !sawTeam;
      setHidden(t, hide2);
    }

    // If the active mobile tab is the one we just hid, switch to the
    // first remaining visible tab so the pane region isn't blank.
    var activeTab = document.querySelector('.qc-tab.is-active');
    if (activeTab && activeTab.hasAttribute('hidden')) {
      var firstVisibleTab = document.querySelector('.qc-tab:not([hidden])');
      if (firstVisibleTab && firstVisibleTab !== activeTab) firstVisibleTab.click();
    }
  }

  function inspectSvgText(svgText) {
    if (!svgText) return;
    var changed = false;
    if (!sawTeam && TEAM_RE.test(svgText)) { sawTeam = true; changed = true; }
    if (!sawLogo && LOGO_RE.test(svgText)) { sawLogo = true; changed = true; }
    if (changed) applyVisibility();
  }

  function inspectThumbnailKey(key) {
    if (!key || inspectedKeys[key]) return;
    inspectedKeys[key] = true;

    var SvgEngine = window.TeamDesignSvg;
    if (!SvgEngine || !SvgEngine.fetch) return;

    // Hot path: SvgEngine may already have the SVG cached (e.g. the
    // colorful-swatch banner-default fetched it). getCached returns the
    // cached text synchronously when present so we don't kick a second
    // fetch for the same key.
    var cached = SvgEngine.getCached && SvgEngine.getCached(key);
    if (typeof cached === 'string') { inspectSvgText(cached); return; }

    SvgEngine.fetch(key).then(inspectSvgText);
  }

  function evaluate() {
    if (sawTeam && sawLogo) return; // ratchet pinned, nothing more to check

    var swatches = document.querySelectorAll('.card-color-swatch[data-thumbnail]');
    for (var i = 0; i < swatches.length; i++) {
      // One swatch per card is enough — different swatches on the same
      // card use the same pattern SVG (only colors differ). Pick the
      // card's first swatch so we converge on the smallest fetch set.
      var card = swatches[i].closest('.card-wrapper');
      if (card && card.dataset.qcSlotProbed === '1') continue;
      if (card) card.dataset.qcSlotProbed = '1';

      inspectThumbnailKey(swatches[i].getAttribute('data-thumbnail') || '');
      if (sawTeam && sawLogo) break;
    }
  }

  function init() {
    // Start with both inputs hidden. They un-hide the moment a probed
    // SVG confirms the matching placeholder. The page rarely lacks a
    // #team rect entirely, so the team input typically un-hides within
    // a few hundred ms (one network round-trip on first visit, instant
    // from cache on subsequent visits).
    applyVisibility();
    evaluate();

    var root = document.querySelector('.product-grid-container') ||
               document.getElementById('product-grid') ||
               document.body;
    if (!root) return;

    // Re-evaluate when team-design.swatches.js populates
    // `.card-color-swatches__colors` (the swatches that carry the
    // `data-thumbnail` we read), or when pagination / AJAX inserts new
    // cards into the grid. The MutationObserver target list is broad
    // because each card's swatches finish loading at its own pace.
    var observer = new MutationObserver(function () { evaluate(); });
    observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
